import { Alert, Button, FormField, Input, Stack } from '@ui';
import { lazy, Suspense, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { formErrors } from '../../../lib/errors';
import { useMeta } from '../../system/useMeta';
import { useLogin } from '../api';
import { PasswordInput } from '../components/PasswordInput';
import { PublicPage } from '../components/PublicPage';
import { safeNext } from '../session';

const HoneycombBackground = lazy(() =>
  import('@ui/organisms/HoneycombBackground').then((m) => ({ default: m.HoneycombBackground })),
);

/** Sign-in to the business of this instance. Accounts exist by invitation only. */
export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('weiter'));
  const meta = useMeta();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password }, { onSuccess: () => navigate(next, { replace: true }) });
  };

  const notSetUp = meta.isSuccess && !meta.data.business_name;
  const errors = formErrors(login.error, { email: ['email.'] });

  return (
    <PublicPage
      title={t('auth.login.title')}
      background={
        <Suspense fallback={null}>
          <HoneycombBackground />
        </Suspense>
      }
    >
      {notSetUp && (
        <Alert tone="warning" title={t('auth.login.notSetUpTitle')}>
          {t('auth.login.notSetUp')}
        </Alert>
      )}
      <Stack as="form" gap={4} onSubmit={onSubmit} noValidate>
        {errors.general && <Alert tone="danger">{errors.general}</Alert>}
        <FormField label={t('auth.email')} error={errors.field.email} required>
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </FormField>
        <FormField
          label={t('auth.password')}
          required
          labelAside={<Link to="/passwort-vergessen">{t('auth.login.forgot')}</Link>}
        >
          {(p) => (
            <PasswordInput {...p} value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
        </FormField>
        <Button type="submit" size="lg" fullWidth loading={login.isPending}>
          {t('auth.login.submit')}
        </Button>
      </Stack>
    </PublicPage>
  );
}
